import React, { useEffect, useMemo, useState } from "react";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";

const TIER_COLORS = {
  S: "bg-gradient-to-br from-yellow-400 to-orange-500 text-black",
  A: "bg-gradient-to-br from-neon to-blue-500 text-black",
  B: "bg-gradient-to-br from-emerald-400 to-teal-500 text-black",
};

export default function MetaBuildsPage() {
  const { t } = useT();
  const [builds, setBuilds] = useState([]);
  const [game, setGame] = useState("all");

  useEffect(() => {
    api.get("/builds").then((r) => setBuilds(r.data.items));
  }, []);

  const gamesInData = useMemo(
    () => Array.from(new Set(builds.map((b) => b.game))),
    [builds]
  );
  const filtered = useMemo(
    () => (game === "all" ? builds : builds.filter((b) => b.game === game)),
    [builds, game]
  );

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          kicker={t("page.builds.kicker")}
          title={t("page.builds.title")}
          subtitle={t("page.builds.sub")}
        />

        <div className="flex flex-wrap gap-2 mb-6" data-testid="builds-game-filter">
          <FilterChip value="all" active={game === "all"} onClick={() => setGame("all")}>
            {t("page.builds.all")}
          </FilterChip>
          {gamesInData.map((g) => (
            <FilterChip key={g} value={g} active={game === g} onClick={() => setGame(g)}>
              {g}
            </FilterChip>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {filtered.map((b, i) => (
            <article
              key={b.id}
              className="card-interactive rounded-2xl overflow-hidden border border-white/5 bg-zinc-900/50 animate-fade-up"
              style={{ animationDelay: `${i * 50}ms` }}
              data-testid={`build-${b.id}`}
            >
              <div className="relative h-40 overflow-hidden">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                <span
                  className={`absolute top-3 right-3 w-10 h-10 rounded-xl grid place-items-center font-display font-black text-xl ${TIER_COLORS[b.tier] || TIER_COLORS.A}`}
                >
                  {b.tier}
                </span>
                <div className="absolute bottom-3 left-3 flex items-center gap-2">
                  <Badge className="bg-black/70 border-white/10 text-zinc-200 backdrop-blur">{b.game}</Badge>
                  <Badge className="bg-neon/10 border-neon/30 text-neon">{b.role}</Badge>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg font-bold">{b.title}</h3>
                <p className="mt-1 text-sm text-zinc-400">{b.description}</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <span className="text-zinc-500">Winrate</span>
                  <span className="font-mono text-neon font-bold">{b.winrate}</span>
                </div>
                <ul className="mt-3 space-y-1.5">
                  {b.items.map((it) => (
                    <li
                      key={it}
                      className="text-sm flex items-center gap-2 rounded-lg bg-zinc-950/60 px-3 py-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-neon" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </PageContainer>
    </AppShell>
  );
}

function FilterChip({ active, onClick, children, value }) {
  return (
    <button
      onClick={onClick}
      data-testid={`chip-${value}`}
      className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? "bg-neon text-black border-neon"
          : "bg-white/5 text-zinc-300 border-white/10 hover:border-white/20"
      }`}
    >
      {children}
    </button>
  );
}
